import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const SITE = "Photosynthetic";
const SITE_ORIGIN = "https://www.photosynthetic.com";
const DEFAULT_TITLE = "Dynamic LED Grow Lights & Synthia™";
const DEFAULT_DESCRIPTION =
  "Dynamic LED grow lights and Synthia™ control software for commercial greenhouses, vertical farms, and research institutions.";
const DEFAULT_OG_IMAGE = "/og-image.jpg";

const upsertMeta = (selector: string, attr: "name" | "property", key: string, value: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const upsertCanonical = (href: string) => {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = href;
};

/**
 * Sets <title>, meta description, canonical URL, OG/Twitter tags, and <html lang>.
 * Backwards-compatible: existing call sites passing only a title still work.
 */
export const useDocumentTitle = (
  title?: string,
  description?: string,
  ogType: "website" | "article" = "website",
  image?: string,
) => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] ?? "en";

  useEffect(() => {
    const fullTitle = title ? `${SITE} | ${title}` : `${SITE} | ${DEFAULT_TITLE}`;
    document.title = fullTitle;

    const desc = description ?? DEFAULT_DESCRIPTION;
    upsertMeta('meta[name="description"]', "name", "description", desc);
    upsertMeta('meta[property="og:title"]', "property", "og:title", fullTitle);
    upsertMeta('meta[property="og:description"]', "property", "og:description", desc);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", fullTitle);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", desc);
    upsertMeta('meta[property="og:type"]', "property", "og:type", ogType);

    const canonical = `${SITE_ORIGIN}${window.location.pathname}`;
    upsertCanonical(canonical);
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);

    const imgPath = image ?? DEFAULT_OG_IMAGE;
    const imgUrl = imgPath.startsWith("http") ? imgPath : `${SITE_ORIGIN}${imgPath}`;
    upsertMeta('meta[property="og:image"]', "property", "og:image", imgUrl);
    upsertMeta('meta[property="og:image:width"]', "property", "og:image:width", "1200");
    upsertMeta('meta[property="og:image:height"]', "property", "og:image:height", "630");
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", imgUrl);
    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");

    document.documentElement.lang = lang;
  }, [title, description, ogType, image, lang]);
};